import { apiFetch } from './client.js';
import {
  PARAMETROS_CHEQUEO_PLANO_DEFAULT,
  normalizarParametrosChequeoPlano,
  type ParametrosChequeoPlano,
  PARAMETROS_CAMBIO_DE_PLANO_DEFAULT,
  normalizarParametrosCambioDePlano,
  type ParametrosCambioDePlano,
  normalizarParametrosMedicionIfc,
  PARAMETROS_MEDICION_IFC_DEFAULT,
  type ParametrosMedicionIfc,
  type UmbralesCalibracion,
} from '@supernova/shared';

export interface FactorCalibracion {
  id: string;
  nombre: string;
  tipo_regla?: string;
  disciplinas?: string[];
  pega_en?: string[];
  ambito?: string;
  activo_default?: boolean;
  /** Estado global del factor (Ecosoft): false = deshabilitado en el motor. Ausente = activo. */
  activo?: boolean;
  condicion_disparo?: { variable: string; operador: string; umbral: unknown };
  ajuste?: { rango_min_pct?: number; rango_max_pct?: number; default_pct?: number };
  formula?: string;
  a_calibrar?: boolean;
  [key: string]: unknown;
}

export interface CatalogoFactores {
  $schema_version?: string;
  nivel_proyecto?: { factores: FactorCalibracion[] };
  nivel_concepto?: {
    factores_rendimiento: FactorCalibracion[];
    factores_materiales?: FactorCalibracion[];
    factores_herramienta?: FactorCalibracion[];
  };
  [key: string]: unknown;
}

export interface CalibracionDoc {
  combinacion: { metodo: string; tope: number };
  /** Umbrales EFECTIVOS (el GET ya los completa con los defaults, igual que el worker). */
  umbrales: UmbralesCalibracion;
  factores: CatalogoFactores;
}

export interface CalibracionVigente {
  version: string;
  vigente_desde: string;
  calibracion: CalibracionDoc;
  created_at: string;
}

export interface CalibracionHistorialItem {
  version: string;
  vigente_desde: string;
  is_current: boolean;
  created_by: string | null;
  created_at: string;
}

export async function getCalibracionVigente(): Promise<CalibracionVigente> {
  return apiFetch('/api/v1/staff/calibracion');
}

export async function getCalibracionHistorial(): Promise<{ items: CalibracionHistorialItem[] }> {
  return apiFetch('/api/v1/staff/calibracion/historial');
}

export async function publicarCalibracion(
  doc: CalibracionDoc,
): Promise<{ ok: true; version: string; id: string }> {
  return apiFetch('/api/v1/staff/calibracion', { method: 'PUT', body: doc });
}

/**
 * Parámetros de medición de IFC de la calibración vigente (visor de planos). Si la API no responde, los
 * defaults — medir con el default es mejor que no medir, y queda avisado en consola.
 */
export async function getParametrosMedicionIfc(): Promise<ParametrosMedicionIfc> {
  try {
    return normalizarParametrosMedicionIfc(await apiFetch<unknown>('/api/v1/ifc/parametros-medicion'));
  } catch (e) {
    console.warn('[medicionIfc] No se pudieron leer los parámetros de la calibración; se usan los defaults.', e);
    return { ...PARAMETROS_MEDICION_IFC_DEFAULT };
  }
}

/** Umbrales de «¿Este plano sirve para presupuestar?» de la calibración vigente. Sin API, los defaults. */
export async function getParametrosChequeoPlano(): Promise<ParametrosChequeoPlano> {
  try {
    return normalizarParametrosChequeoPlano(await apiFetch<unknown>('/api/v1/planos/parametros-chequeo'));
  } catch (e) {
    console.warn('[chequeoDelPlano] No se pudieron leer los umbrales de la calibración; se usan los defaults.', e);
    return { ...PARAMETROS_CHEQUEO_PLANO_DEFAULT };
  }
}

/** Umbrales de «Cambió el plano» (`cambio_*`) de la calibración vigente. Sin API, los defaults. */
export async function getParametrosCambioDePlano(): Promise<ParametrosCambioDePlano> {
  try {
    return normalizarParametrosCambioDePlano(await apiFetch<unknown>('/api/v1/planos/parametros-cambio'));
  } catch (e) {
    console.warn('[cambioDePlano] No se pudieron leer los umbrales de la calibración; se usan los defaults.', e);
    return { ...PARAMETROS_CAMBIO_DE_PLANO_DEFAULT };
  }
}
